import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Label, Text } from 'components/Typography';
import { lineHeight, theme, toRem } from 'design';
import { CalculatorParticipantType, DisbursementMethod, IPayoutStats } from 'types';
import { sumBy } from 'utils/arrays';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay } from 'utils/currency';

interface IPaymentOverviewStatsProps {
  payoutStats?: IPayoutStats;
  hideHouseAccount?: boolean;
  isLoading: boolean;
}

const PaymentTotals = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  width: 100%;
  border-radius: ${toRem(8)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  padding: ${toRem(theme.space.lg)} !important;
  margin-bottom: ${toRem(theme.space.xl)};
  background-color: ${theme.color.neutral[50]};

  > p {
    text-transform: uppercase;
  }
`;

// One stat per row on a phone, label left and value right; a wrapping row of label-over-value tiles from the small breakpoint up.
const TotalRow = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  margin-top: ${toRem(theme.space.lg)};
  gap: ${toRem(theme.space.md)};

  label {
    text-transform: capitalize;
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    color: ${theme.color.neutral[500]};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-flow: row wrap;
    justify-content: center;
  }
`;

const TotalValue = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.xs)};

  p {
    font-size: ${toRem(theme.font.size.lg)};
    line-height: ${lineHeight(theme.font.size.lg)};
    font-weight: ${theme.font.weight.semiBold};
  }

  .react-loading-skeleton {
    border-radius: ${toRem(12)};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-flow: column nowrap;
    flex: 1 1 auto;
  }
`;

export const PaymentOverviewStats: React.FC<IPaymentOverviewStatsProps> = ({ payoutStats, hideHouseAccount = false, isLoading }) => {
  const paymentStatsByDate = payoutStats?.byDate;
  const grossTotal = paymentStatsByDate ? sumBy(paymentStatsByDate, 'gross') : 0;
  const netTotal = paymentStatsByDate ? sumBy(paymentStatsByDate, 'net') : 0;
  const holdbackTotal = paymentStatsByDate ? sumBy(paymentStatsByDate, 'holdbacks') : 0;
  const instantTotal = paymentStatsByDate ? sumBy(paymentStatsByDate, DisbursementMethod.INSTANT) : 0;
  const payrollTotal = paymentStatsByDate ? sumBy(paymentStatsByDate, DisbursementMethod.PAYROLL) : 0;
  const cashTotal = paymentStatsByDate ? sumBy(paymentStatsByDate, DisbursementMethod.CASH) : 0;
  const autogratsTotal = paymentStatsByDate ? sumBy(paymentStatsByDate, 'autoGratuity') : 0;
  const tipsTotal = grossTotal - autogratsTotal || 0;

  const calculateHouseTotals = useMemo(() => {
    if (!payoutStats?.byParticipant?.length || !payoutStats?.byParticipant.some((stats) => stats.type === CalculatorParticipantType.HOUSE)) return undefined;

    return payoutStats?.byParticipant.filter((stats) => stats.type === CalculatorParticipantType.HOUSE).reduce((sum, item) => sum + (item.total ?? 0), 0);
  }, [payoutStats]);

  const stats = [
    { label: 'Tips', value: centsToDollarsDisplay(tipsTotal) },
    { label: 'Autograts', value: centsToDollarsDisplay(autogratsTotal) },
    { label: 'Gross', value: centsToDollarsDisplay(grossTotal) },
    { label: 'Holdback', value: centsToNegativeDollarsDisplay(holdbackTotal) },
    { label: 'Net', value: centsToDollarsDisplay(netTotal) },
    { label: 'Payroll', value: centsToDollarsDisplay(payrollTotal) },
    !hideHouseAccount && calculateHouseTotals >= 0 && { label: 'House Account', value: centsToDollarsDisplay(calculateHouseTotals) },
    { label: 'Cash', value: centsToDollarsDisplay(cashTotal) },
    { label: 'Instant', value: centsToDollarsDisplay(instantTotal) },
  ];

  const mapStats = () =>
    stats
      .filter((stat) => !!stat)
      .map((stat) => (
        <TotalValue data-cy={`stats-${stat.label.toLowerCase().replace(/\s/g, '')}`} key={stat.label}>
          <Label>{stat.label}</Label>
          {isLoading ? <Skeleton width={100} className="value" /> : <Text size="small">{stat.value}</Text>}
        </TotalValue>
      ));

  return (
    <PaymentTotals data-cy="payment-totals">
      <Text size="xsmall" fontWeight="semibold" capitalize>
        Payment Overview
      </Text>
      <TotalRow>{mapStats()}</TotalRow>
    </PaymentTotals>
  );
};
